import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import DeleteSweepRoundedIcon from '@mui/icons-material/DeleteSweepRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { RiskClassification } from 'app/config/constants';
import { TruckGroupSelectionField } from 'app/shared/components/truck-group-selection/TruckGroupSelectionField';
import { RiskClassificationSelector } from 'app/shared/components/risk-classification/RiskClassificationSelector';
import { CustomSwitch, FormControlDefault, TextFieldForms, TypographyForms } from 'app/shared/layout/register/DefaultFormsComponents';
import React from 'react';

const MIN_POINTS = 3;
const MAX_POINTS = 10;

interface Props {
  onSave: (values: { name: string; description: string; riskClassification: RiskClassification; notificationEnabled: boolean }) => void;
  name: string;
  setName: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  riskClassification: RiskClassification;
  setRiskClassification: (v: RiskClassification) => void;
  notificationEnabled: boolean;
  setNotificationEnabled: (v: boolean) => void;
  selectedGroupIds: number[];
  setSelectedGroupIds: (ids: number[]) => void;
  pointCount: number;
  polygonClosed: boolean;
  onUndoPoint: () => void;
  onClearPoints: () => void;
  readOnly?: boolean;
}

export const VirtualFencePolygonForms = ({
  onSave,
  name,
  setName,
  description,
  setDescription,
  riskClassification,
  setRiskClassification,
  notificationEnabled,
  setNotificationEnabled,
  selectedGroupIds,
  setSelectedGroupIds,
  pointCount,
  polygonClosed,
  onUndoPoint,
  onClearPoints,
  readOnly = false,
}: Props) => {
  const theme = useTheme();
  const iconDisabledColor = theme.palette.mode === 'dark' ? 'rgba(171,173,198,0.3)' : 'rgba(0,0,0,0.26)';

  const tooFew = pointCount < MIN_POINTS;
  const tooMany = pointCount > MAX_POINTS;
  const pointsValid = !tooFew && !tooMany;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (readOnly) return;
    if (!name?.trim() || !pointsValid) return;
    onSave({ name: name.trim(), description, riskClassification, notificationEnabled });
  };

  const formControlSx = { my: 0, width: '100%' };

  const pointsColor = tooMany ? 'error.main' : pointsValid && pointCount > 0 ? 'success.main' : 'text.disabled';
  const pointsHint = tooMany
    ? `máx. ${MAX_POINTS}`
    : pointCount === 0
    ? `mín. ${MIN_POINTS}`
    : tooFew
    ? `faltam ${MIN_POINTS - pointCount}`
    : polygonClosed
    ? 'fechado'
    : 'aberto';

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', height: '100%' }}
    >
      {/* Dados */}
      <Box display="flex" flexDirection="column" gap={1.5}>
        <FormControlDefault variant="outlined" sx={formControlSx}>
          <TypographyForms variant="body2">Nome *</TypographyForms>
          <TextFieldForms
            id="virtual-fence-polygon-name"
            name="name"
            data-cy="name"
            size="small"
            value={name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            placeholder="Nome da cerca"
            disabled={readOnly}
            required
            fullWidth
          />
        </FormControlDefault>

        <FormControlDefault variant="outlined" sx={formControlSx}>
          <TypographyForms variant="body2">Descrição</TypographyForms>
          <TextFieldForms
            id="virtual-fence-polygon-description"
            name="description"
            data-cy="description"
            size="small"
            value={description}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)}
            placeholder="Descrição"
            disabled={readOnly}
            multiline
            minRows={1}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': { alignItems: 'flex-start', py: '10px' },
              '& textarea.MuiInputBase-inputMultiline': { p: '0 !important', lineHeight: 1.4 },
            }}
          />
        </FormControlDefault>

        {/* Pontos + ações */}
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          <TypographyForms variant="body2">Pontos</TypographyForms>
          <Box
            role="status"
            aria-label={`${pointCount} ${pointCount === 1 ? 'ponto' : 'pontos'}. Mínimo ${MIN_POINTS}, máximo ${MAX_POINTS}.`}
            sx={{
              height: 43,
              px: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              border: '1px solid',
              borderColor: tooMany ? 'error.main' : 'divider',
              borderRadius: '8px',
              bgcolor: 'action.hover',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.25, flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: pointsColor, whiteSpace: 'nowrap', lineHeight: 1 }}>
                {pointCount}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 400, color: 'text.disabled', whiteSpace: 'nowrap', fontSize: '0.7rem' }}>
                /{MAX_POINTS}
              </Typography>
              <Typography variant="caption" sx={{ color: tooMany ? 'error.main' : 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.7rem', ml: 0.5 }}>
                {pointsHint}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
              <Tooltip title="Desfazer último ponto" placement="top">
                <span>
                  <IconButton
                    size="small"
                    onClick={onUndoPoint}
                    disabled={readOnly || pointCount === 0}
                    aria-label="Desfazer último ponto"
                    sx={{ color: readOnly || pointCount === 0 ? `${iconDisabledColor} !important` : `${theme.palette.secondary.main} !important` }}
                  >
                    <UndoRoundedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Limpar todos os pontos" placement="top">
                <span>
                  <IconButton
                    size="small"
                    onClick={onClearPoints}
                    disabled={readOnly || pointCount === 0}
                    aria-label="Limpar todos os pontos"
                    sx={{ color: readOnly || pointCount === 0 ? `${iconDisabledColor} !important` : `${theme.palette.error.main} !important` }}
                  >
                    <DeleteSweepRoundedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </Box>

      <Divider />

      <Box display="flex" flexDirection="column" gap={1.5}>
        <FormControlDefault variant="outlined" sx={formControlSx}>
          <TypographyForms variant="body2" sx={{ mb: 0.5 }}>Classificação *</TypographyForms>
          <RiskClassificationSelector value={riskClassification} onChange={setRiskClassification} disabled={readOnly} />
        </FormControlDefault>

        <FormControlLabel
          control={
            <CustomSwitch
              checked={notificationEnabled}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNotificationEnabled(e.target.checked)}
              disabled={readOnly}
              size="small"
              data-cy="notificationEnabled"
            />
          }
          label={
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Receber notificação ao entrar/sair da cerca
            </Typography>
          }
          sx={{ ml: '-2px', mt: 0.5 }}
        />
      </Box>

      <Divider />

      <TruckGroupSelectionField selectedGroupIds={selectedGroupIds} onChange={setSelectedGroupIds} disabled={readOnly} />

      <Button
        id="save-entity"
        data-cy="entityCreateSaveButton"
        type="submit"
        variant="contained"
        color="secondary"
        startIcon={<SaveRoundedIcon />}
        disabled={readOnly || !name?.trim() || !pointsValid || !polygonClosed}
        fullWidth
        sx={{ mt: 'auto', borderRadius: '8px', textTransform: 'none' }}
      >
        {readOnly ? 'Somente visualização' : 'Salvar'}
      </Button>
    </Box>
  );
};
